import type { TimePreset } from "@cd/core";
import type { Cadence } from "../../lib/api";

// Wording for scheduled report emails (docs/scheduled-reports.md). Sends happen on the hour in the
// schedule's own time zone, which may not be the viewer's.

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** 0 → "12 AM", 13 → "1 PM". */
export const hourLabel = (h: number) => `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`;

export function ordinal(n: number) {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th";
  return `${n}${suffix}`;
}

/** "Daily at 7 AM", "Mondays at 6 AM", "Monthly on the 31st (or the last day) at 9 PM". */
export function describeCadence(c: Cadence) {
  const at = `at ${hourLabel(c.hour)}`;
  if (c.kind === "daily") return `Daily ${at}`;
  if (c.kind === "weekly") return `${WEEKDAYS[c.weekday]}s ${at}`;
  return `Monthly on the ${ordinal(c.day)}${c.day > 28 ? " (or the last day)" : ""} ${at}`;
}

/** "Mon, Oct 20, 7:00 AM" in the viewer's time zone. */
export const formatWhen = (at: string) =>
  new Date(at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** The viewer's time zone, the default for a new schedule. */
export const localTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/** Every IANA time zone the browser knows, with `current` first among them if it's missing. */
export function timeZones(current: string) {
  let zones: string[];
  try {
    zones = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
  } catch {
    zones = [];
  }
  return zones.includes(current) ? zones : [current, ...zones];
}

/** A new schedule's date range until the owner picks one: the period since the last send (as the worker's). */
export const defaultPreset = (c: Pick<Cadence, "kind">): TimePreset =>
  c.kind === "daily" ? "yesterday" : c.kind === "weekly" ? "last_week" : "last_month";

/** Ranges worth emailing on a schedule; "Today" would only ever be part of a day. */
export const SCHEDULE_PRESETS: TimePreset[] = ["yesterday", "last_7_days", "last_week", "last_30_days", "last_90_days", "month_to_date", "last_month"];
